
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, LayoutDashboard } from "lucide-react";
import BubbleCareLogo from "./BubbleCareLogo";

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navigation = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: "Emergency", path: "/emergency" },
    { name: "My Bubble", path: "/bubble" },
    { name: "Admin", path: "/admin", icon: <LayoutDashboard className="w-4 h-4 mr-1" /> },
  ];

  const headerClasses = `fixed top-0 inset-x-0 z-50 transition-all duration-300 ease-in-out ${
    isScrolled
      ? "py-3 glass-morphism shadow-sm"
      : "py-5 bg-transparent"
  }`;

  return (
    <header className={headerClasses}>
      <div className="container-custom flex justify-between items-center">
        <Link 
          to="/" 
          className="flex items-center"
        >
          <BubbleCareLogo />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-8">
          {navigation.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`font-medium transition-colors duration-200 flex items-center ${
                location.pathname === item.path
                  ? "text-tech-600"
                  : "text-tech-800 hover:text-tech-600"
              }`}
            >
              {item.icon && item.icon}
              {item.name}
            </Link>
          ))}
          <Button className="btn-primary">
            Get Started
          </Button>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden flex items-center"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle mobile menu"
        >
          {isMobileMenuOpen ? (
            <X className="w-6 h-6 text-tech-800" />
          ) : (
            <Menu className="w-6 h-6 text-tech-800" />
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2, ease: [0.45, 0, 0.2, 1] }}
            className="md:hidden glass-morphism shadow-md"
            style={{ position: 'absolute', width: '100%', top: '100%', zIndex: 40 }}
          >
            <div className="container-custom py-5 space-y-3">
              {navigation.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center block py-2 px-4 font-medium rounded-lg transition-colors duration-200 ${
                    location.pathname === item.path
                      ? "bg-tech-50 text-tech-600"
                      : "text-tech-800 hover:bg-tech-50 hover:text-tech-600"
                  }`}
                >
                  {item.icon && item.icon}
                  {item.name}
                </Link>
              ))}
              <div className="pt-2 pb-1">
                <Button className="btn-primary w-full">
                  Get Started
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
